import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, ClipboardList, Search, X } from 'lucide-react';
import type { LodgementRecord } from '../../hooks/useLodgements';
import { Pagination } from '../../components/common/Pagination';
import { SkeletonTableRow } from '../../components/common/SkeletonLoader';
import {
  money, fmtDate, statusClass, labelFor, STATUS_OPTIONS, EMPTY,
} from './LodgementDisplay';

/**
 * The lodgement register.
 *
 * IT IS A STANDARD TABLE, NOT A BESPOKE ONE. Sortable headers, a filter bar and
 * the shared `Pagination` control are what every other table in this app has,
 * and the mechanics here are lifted from `components/loans/LoanListTable.tsx`
 * deliberately rather than re-invented: same header button, same stacked-arrow
 * affordance on an unsorted column, same click-to-toggle, same cell padding and
 * row hover. Someone who uses Loan Portfolio daily should not have to learn
 * this screen.
 *
 * SORTING AND FILTERING SEE THE WHOLE PERIOD, NEVER A PAGE. `rows` is the
 * complete window (`useLodgements` pages it in full before handing it over), so
 * "sort by amount" really does surface the largest deal in the period rather
 * than the largest one that happened to be downloaded. Pagination is therefore
 * a presentation step applied last: filter, then sort, then slice.
 *
 * The one behaviour that differs from Loan Portfolio is where the work happens.
 * Loan Portfolio sorts and pages server-side over a BigQuery table of every
 * loan ever settled; a lodgement register is a hand-kept book of a few hundred
 * rows a period, already in memory, so a round trip per sort would be latency
 * bought for nothing.
 */

interface Props {
  /** The complete period, already fetched. Not a page. */
  rows: LodgementRecord[];
  /** False when the fetch cap bit or a page failed: totals are understated. */
  rowsComplete: boolean;
  loading: boolean;
  /** Pages after the first are still arriving. */
  loadingRest: boolean;
  /** The owner column is noise for a reader who only ever sees their own rows. */
  showOwner: boolean;
  ownerLabels: Map<string, string>;
  canEnter: boolean;
  onEdit: (r: LodgementRecord) => void;
}

interface Filters {
  search: string;
  status: string;
  lender: string;
  mir: 'all' | 'yes' | 'no';
}

const EMPTY_FILTERS: Filters = { search: '', status: '', lender: '', mir: 'all' };

type SortDirection = 'asc' | 'desc';
interface Sort {
  column: string;
  direction: SortDirection;
}

/** Sortable value for one column. `null` sorts last in BOTH directions. */
type SortValue = string | number | null;

interface Column {
  key: string;
  label: string;
  align?: 'right';
  className?: string;
  /** What the comparator sees. */
  sortValue: (r: LodgementRecord, ownerLabels: Map<string, string>) => SortValue;
  render: (r: LodgementRecord, ownerLabels: Map<string, string>) => React.ReactNode;
}

const selectCls =
  'w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';

/**
 * Every column is sortable. The founder's objection was that the register had
 * no sortable headers at all, and a table where some headers respond and others
 * do not is a worse answer than one where they all do.
 */
function buildColumns(showOwner: boolean): Column[] {
  const cols: Column[] = [
    {
      key: 'date_lodged',
      label: 'Date lodged',
      sortValue: (r) => r.dateLodged || null,
      render: (r) => fmtDate(r.dateLodged),
    },
  ];

  if (showOwner) {
    cols.push({
      key: 'who',
      label: 'Who',
      className: 'max-w-[12rem]',
      // Sorts on what the reader can actually see. An unresolved ref shows the
      // ref itself rather than a made-up label: it names a real condition (a
      // departed member, a deactivated analyst record) and "Unknown" would hide
      // which row needs attention.
      sortValue: (r, labels) => labels.get(r.ownerId) ?? r.ownerId ?? null,
      render: (r, labels) => (
        <span className="block truncate" title={labels.get(r.ownerId) ?? r.ownerId}>
          {labels.get(r.ownerId) ?? r.ownerId ?? EMPTY}
        </span>
      ),
    });
  }

  cols.push(
    {
      key: 'client',
      label: 'Client',
      className: 'max-w-[14rem]',
      sortValue: (r) => r.clientName || null,
      render: (r) => (
        <span className="block truncate font-medium text-white" title={r.clientName}>
          {r.clientName}
        </span>
      ),
    },
    {
      key: 'lender',
      label: 'Lender',
      sortValue: (r) => r.lender || null,
      render: (r) => r.lender || EMPTY,
    },
    {
      key: 'amount',
      label: 'Amount',
      align: 'right',
      sortValue: (r) => r.lodgeAmountCents ?? null,
      render: (r) => <span className="tabular-nums text-white">{money(r.lodgeAmountCents)}</span>,
    },
    {
      key: 'to_formal',
      label: 'To formal',
      align: 'right',
      sortValue: (r) => r.daysToFormal,
      render: (r) => (
        <span className="tabular-nums">{r.daysToFormal === null ? EMPTY : `${r.daysToFormal}d`}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortValue: (r) => labelFor(r.status),
      render: (r) => (
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusClass(r.status)}`}>
          {labelFor(r.status)}
        </span>
      ),
    },
    {
      key: 'mir',
      label: 'MIR',
      align: 'right',
      sortValue: (r) => r.mirCount ?? 0,
      render: (r) => <span className="tabular-nums">{r.mirCount || EMPTY}</span>,
    },
    {
      key: 'settled',
      label: 'Settled',
      align: 'right',
      sortValue: (r) => r.settledDate,
      render: (r) => fmtDate(r.settledDate),
    },
    {
      key: 'settled_amt',
      label: 'Settled amt',
      align: 'right',
      sortValue: (r) => r.settledAmountCents,
      render: (r) => <span className="tabular-nums">{money(r.settledAmountCents)}</span>,
    }
  );

  return cols;
}

/**
 * Nullish always sorts last, whichever way the column is pointing.
 *
 * A blank "Settled" is not a very small date, and letting it sort as one would
 * bury every settled deal under the unsettled ones the moment someone clicked
 * the column to find them.
 */
function compare(a: SortValue, b: SortValue, direction: SortDirection): number {
  if (a === b) return 0;
  if (a === null || a === '') return 1;
  if (b === null || b === '') return -1;
  const sign = direction === 'asc' ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * sign;
  return String(a).localeCompare(String(b), 'en-AU') * sign;
}

export function LodgementRegister({
  rows, rowsComplete, loading, loadingRest, showOwner, ownerLabels, canEnter, onEdit,
}: Props): React.ReactElement {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [sort, setSort] = useState<Sort>({ column: 'date_lodged', direction: 'desc' });
  const [page, setPage] = useState(1);
  // Matches the app's other tables (`useLoanList`), and the shared control
  // offers the same 25 / 50 / 100 choices from here.
  const [pageSize, setPageSize] = useState(50);

  const columns = useMemo(() => buildColumns(showOwner), [showOwner]);

  /** Lenders actually present in this period, not a global list. */
  const lenders = useMemo(() => {
    const seen = new Set<string>();
    for (const r of rows) if (r.lender) seen.add(r.lender);
    return Array.from(seen).sort((a, b) => a.localeCompare(b, 'en-AU'));
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = filters.search.trim().toLowerCase();
    return rows.filter((r) => {
      if (needle && !r.clientName.toLowerCase().includes(needle)) return false;
      if (filters.status && r.status !== filters.status) return false;
      if (filters.lender && r.lender !== filters.lender) return false;
      if (filters.mir === 'yes' && !r.mirCount) return false;
      if (filters.mir === 'no' && r.mirCount > 0) return false;
      return true;
    });
  }, [rows, filters]);

  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sort.column);
    if (!col) return filtered;
    // Copy first: Array.prototype.sort mutates, and `filtered` is memoised.
    return [...filtered].sort((a, b) =>
      compare(col.sortValue(a, ownerLabels), col.sortValue(b, ownerLabels), sort.direction)
    );
  }, [filtered, columns, sort, ownerLabels]);

  /**
   * Clamped rather than reset by an effect. Filtering down to fewer pages while
   * the reader sits on page 4 must not leave them staring at an empty table,
   * and deriving the safe page makes that impossible by construction instead of
   * repairing it one render later.
   */
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const visible = useMemo(
    () => sorted.slice((safePage - 1) * pageSize, safePage * pageSize),
    [sorted, safePage, pageSize]
  );

  const updateFilters = (patch: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };
  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const onSortChange = (column: string) => {
    setSort((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : // A new column opens descending, matching every other table here.
          { column, direction: 'desc' }
    );
  };

  const hasActiveFilters =
    Boolean(filters.search) || Boolean(filters.status) || Boolean(filters.lender) || filters.mir !== 'all';

  return (
    <>
      <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700">
        <div className="mb-4">
          <h2 className="text-base font-semibold text-white">Lodgement register</h2>
          <p className="mt-0.5 text-[11px] text-gray-400">
            Time to formal is computed from the dates. Settlement columns arrive
            from the commission pipeline, not from entry. Sorting and filtering
            cover the whole period, not the page on screen.
            {canEnter ? ' Select a row to edit it.' : ''}
          </p>
        </div>

        <div className="flex gap-3 items-center">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              aria-label="Search by client name"
              placeholder="Search by client name..."
              value={filters.search}
              onChange={(e) => updateFilters({ search: e.target.value })}
              className="block w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-sm"
            />
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <X className="h-4 w-4" />
              Clear
            </button>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label htmlFor="lodgement-status" className="block text-sm font-medium text-gray-300 mb-2">
              Status
            </label>
            <select
              id="lodgement-status"
              value={filters.status}
              onChange={(e) => updateFilters({ status: e.target.value })}
              className={selectCls}
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="lodgement-lender" className="block text-sm font-medium text-gray-300 mb-2">
              Lender
            </label>
            <select
              id="lodgement-lender"
              value={filters.lender}
              onChange={(e) => updateFilters({ lender: e.target.value })}
              className={selectCls}
            >
              <option value="">All lenders</option>
              {lenders.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="lodgement-mir" className="block text-sm font-medium text-gray-300 mb-2">
              MIR
            </label>
            <select
              id="lodgement-mir"
              value={filters.mir}
              onChange={(e) => updateFilters({ mir: e.target.value as Filters['mir'] })}
              className={selectCls}
            >
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-400">
          <span>
            {loading
              ? 'Loading the period...'
              : `${sorted.length.toLocaleString()} of ${rows.length.toLocaleString()} lodgement${rows.length === 1 ? '' : 's'} in this period`}
          </span>
          {loadingRest && <span className="text-amber-300">still loading later pages</span>}
          {/*
            Say so when the window is short. Every figure on this page derives
            from these rows, so an unstated cap would read as a complete total
            that is quietly wrong.
          */}
          {!loading && !loadingRest && !rowsComplete && (
            <span className="text-amber-300">
              partial period: more rows exist than could be loaded in one pass
            </span>
          )}
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900 border-b border-gray-700">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    aria-sort={
                      sort.column === column.key
                        ? sort.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                    className={`px-6 py-4 text-xs font-semibold text-gray-300 uppercase tracking-wider ${
                      column.align === 'right' ? 'text-right' : 'text-left'
                    } ${column.className || ''}`}
                  >
                    <button
                      type="button"
                      onClick={() => onSortChange(column.key)}
                      className="group inline-flex items-center gap-1.5 hover:text-white transition-colors"
                      title={`Click to sort by ${column.label}`}
                    >
                      {column.label}
                      <span className="flex-shrink-0">
                        {sort.column === column.key ? (
                          sort.direction === 'asc' ? (
                            <ChevronUp className="h-4 w-4 text-blue-400" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-blue-400" />
                          )
                        ) : (
                          // Stacked arrows on every unsorted column, so the
                          // header reads as sortable before it is clicked.
                          <span className="flex flex-col -space-y-2.5 opacity-40 group-hover:opacity-70">
                            <ChevronUp className="h-3 w-3" />
                            <ChevronDown className="h-3 w-3" />
                          </span>
                        )}
                      </span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading &&
                [0, 1, 2, 3, 4].map((i) => <SkeletonTableRow key={i} columns={columns.length} />)}

              {!loading && visible.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-12 text-center">
                    <ClipboardList className="mx-auto h-12 w-12 text-gray-500" />
                    <h3 className="mt-2 text-sm font-medium text-gray-300">
                      {rows.length === 0 ? 'No lodgements found' : 'No lodgements match these filters'}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {rows.length === 0
                        ? canEnter
                          ? 'Nothing has been captured for this period yet.'
                          : 'Nothing to show for your role in this period.'
                        : 'Try adjusting your search or filter criteria.'}
                    </p>
                  </td>
                </tr>
              )}

              {!loading &&
                visible.map((r) => (
                  <tr
                    key={r.id}
                    onClick={canEnter ? () => onEdit(r) : undefined}
                    className={`hover:bg-gray-700 transition-colors ${canEnter ? 'cursor-pointer' : ''}`}
                    title={canEnter ? 'Edit this lodgement' : undefined}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={`px-6 py-4 text-sm text-gray-300 ${
                          column.align === 'right' ? 'text-right' : 'text-left'
                        } ${column.className || ''}`}
                      >
                        {column.render(r, ownerLabels)}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && sorted.length > 0 && (
        <Pagination
          currentPage={safePage}
          totalItems={sorted.length}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(n) => {
            setPageSize(n);
            setPage(1);
          }}
        />
      )}
    </>
  );
}
